import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import { LoaderData } from "~/types";
import { FormattedLeaseData, LeaseRenewalPayload } from "~/types/lease";
import {
  apiClient,
  LeaseUpdateDetailsLoaderResult,
} from "~/services/apiClient";
import { INTENTS, loadCookiesData, resolveActorUserName } from "~/utils";
import * as APITypes from "~/API";

export const LeaseUploadPageLoader = async ({
  request,
  params,
}: LoaderFunctionArgs) => {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) return redirect("/explore");

  const leaseID = params.leaseID || "";
  const result = await apiClient.loader<LeaseUpdateDetailsLoaderResult>(
    "lease-update-details",
    { id: leaseID }
  );

  if (!result.success || !result.data) {
    console.error("Failed to load lease update details:", result.error);
    return redirect("/explore");
  }

  const userName = await resolveActorUserName(user, authAccessInfo);
  const data: LeaseUploadPageLoaderType = {
    unitDetails: { success: true, data: result.data.unitDetails },
    ownerID: user?.uid || "",
    ownerType: APITypes.OwnerType.User,
    userName,
    formattedLease: {
      success: true,
      data: result.data.formattedLease as FormattedLeaseData,
    },
    comment: result.data.comment,
  };

  return data;
};

export type LeaseUploadPageLoaderType = {
  unitDetails: LoaderData<APITypes.Unit>;
  ownerID: string;
  ownerType: APITypes.OwnerType;
  userName: string;
  formattedLease: LoaderData<FormattedLeaseData>;
  comment: string;
};

export const LeaseUploadPageAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");

  const formData = await request.formData();
  const intent = formData.get("intent");

  if (intent === INTENTS._update_renewal_lease) {
    const leaseID = params.leaseID || "";
    const payload = JSON.parse(
      formData.get("body") as string
    ) as LeaseRenewalPayload;

    const result = await apiClient.sendIntent<Record<string, unknown>>(
      INTENTS._update_renewal_lease,
      {
        id: leaseID,
        lease: payload.lease,
        startDatePicker: String(payload.startDatePicker),
        endDatePicker: String(payload.endDatePicker),
        monthlyRentAmount: payload.monthlyRentAmount,
        normalizeRent: Boolean(
          (payload as { normalizeRent?: boolean }).normalizeRent,
        ),
      }
    );
    return result;
  }

  return null;
};
